'use client';

import SvgIcon from '@mui/material/SvgIcon';
import { Stack, Theme, Typography } from '@mui/material';
import { withClarityTheme } from '@verifime/styles';
import { ReactElement, ReactNode } from 'react';
import { ExpiryClock } from '../ExpiryClock/ExpiryClock';

export type LinkSentProps = Readonly<{
  /** "Email sent!" - the heading. Approved copy from the "Link sent, one screen" design board
   *  (settled 2026-09-17), so the caller owns it here as a required prop with no default. */
  heading: string;
  /** "We've sent a link to" - the lead line immediately above the address. Caller-owned. */
  leadLine: string;
  /** The address the link was sent to. Rendered verbatim; this component does not format or
   *  validate it. */
  email: string;
  /** The expiry panel's caption, e.g. "The email link will be valid until". Forwarded to
   *  ExpiryClock's own `label`. Caller-owned. */
  expiryLabel: string;
  /** When the link stops working. Passed straight through to ExpiryClock, so it carries the
   *  same `Date | string` contract - see that component's own doc comment for why an epoch
   *  value must be converted to a Date by the caller rather than passed as a bare number.
   *  LinkSent never computes this itself: see linkExpiry.ts's `computeLinkExpiry` for the pure
   *  helper a caller uses to derive it from the moment it sent the link. */
  expiresAt: Date | string;
  /** The tail line under the expiry panel, e.g. the "check your junk folder" note. Caller-owned. */
  tail: string;
  /** The resend affordance. Caller-supplied (typically composed from ThrottledResend), following
   *  the same rendered-ReactNode-slot pattern as RecoverInvitation's resendAction: this component
   *  places it, but never constructs it (ADR-0050). */
  resend: ReactNode;
  'data-testid'?: string;
}>;

/**
 * One "email sent" screen, replacing four: self sign-up by code, an invitation email that lands
 * on a tracking reference, the wallet widget landing on the same, and a returning customer's
 * login through their authenticator app. All four land here once a magic link has just gone out.
 * Every visible string on the shared shell arrives as a required prop - this component authors
 * none of it - and `resend` is a caller-composed slot this component only places, never builds.
 * Copy and measurements are approved off the "Link sent, one screen" design board (settled
 * 2026-09-17); the token deviations from that board are documented inline below.
 *
 * Wrapped in withClarityTheme for the same reason ExpiryClock is: its surface and permanent-ramp
 * tokens only exist on the Clarity theme, and this component has no control over what theme a
 * future caller renders it under. Nesting withClarityTheme inside an already-Clarity screen (as
 * the ExpiryClock composed below already is) is idempotent, so this costs nothing here.
 */
function LinkSentComponent({
  heading,
  leadLine,
  email,
  expiryLabel,
  expiresAt,
  tail,
  resend,
  'data-testid': dataTestId,
}: LinkSentProps): ReactElement {
  return (
    <Stack
      data-testid={dataTestId}
      sx={(theme: Theme) => ({
        alignItems: 'center',
        textAlign: 'center',
        // Not a token: the board's own stack (getComputedStyle, not a screenshot) measures a
        // 22px row gap. theme.semantic.spacing.base is 16px and semantic.spacing['2lg'] is 24px;
        // rounding up to 24 was tried and reverted (PR 2218 reissued the whole board reference
        // set once that rounding turned out wrong), so 22 ships as a literal, matching neither
        // neighbouring token.
        gap: '22px',
        width: '100%',
        // Caps the content column at the board's own desktop width (600px): the Clarity semantic
        // size scale already carries this value (sizeSm, 37.5rem), the same token
        // MagicLinkContinue.tsx uses for its own card's desktop cap.
        maxWidth: theme.semantic.size.sm,
        // Centres the block within a parent wider than maxWidth: without this, a parent that
        // does not itself centre its children leaves this sitting hard against the left edge
        // once maxWidth clips it below 100%.
        marginInline: 'auto',
      })}
    >
      <SvgIcon
        aria-hidden
        viewBox="0 0 50 50"
        sx={(theme: Theme) => ({
          // 48px, not the board's 50: theme.semantic.size['5xs'] is the nearest token, 2px off
          // the board's export value and invisible at this size. 50 was the Figma export's own
          // number, not a design decision.
          width: theme.semantic.size['5xs'],
          height: theme.semantic.size['5xs'],
          // theme.palette.permanent.primary, NOT theme.palette.text.primary. The board's own
          // stylesheet paints this glyph var(--text-primary), which on the board resolves to
          // the brand violet #5f48fb; MUI's identically-named text.primary is a near-black body
          // colour instead. Same name, opposite value - ExpiryClock.tsx documents the same trap
          // for its own hourglass icon.
          color: theme.palette.permanent.primary,
        })}
      >
        <path d="M4.1875 43.75L47.9167 25L4.1875 6.25L4.16667 20.8333L35.4167 25L4.16667 29.1667L4.1875 43.75Z" />
      </SvgIcon>
      {/* Line height: the board sets 1.25 here (the heading) and 1.4 on the body1/body2 lines
          below (lead line, address, tail). Every Clarity typography variant resolves through
          one lineHeightStandard token, 1.2; the scale steps 1, 1.2, 1.5, 2, so neither board
          value is reachable. Left at the token's 1.2 throughout: the difference is invisible at
          this line length. */}
      <Typography variant="h4">{heading}</Typography>
      <Typography variant="body1">{leadLine}</Typography>
      <Typography
        variant="body2"
        sx={(theme: Theme) => ({
          // No font-family token of any kind exists in the Clarity build, so the mono face is a
          // literal here, matching the board's own --mono custom property
          // ('IBM Plex Mono', ui-monospace, monospace).
          fontFamily: "'IBM Plex Mono', ui-monospace, monospace",
          fontWeight: theme.semantic.fontWeight.semibold,
        })}
      >
        {email}
      </Typography>
      <ExpiryClock label={expiryLabel} expiresAt={expiresAt} />
      <Typography variant="body2" color="text.tertiary">
        {tail}
      </Typography>
      {resend}
    </Stack>
  );
}

export const LinkSent = withClarityTheme(LinkSentComponent);
